import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import HomePage from './pages/HomePage/HomePage';
import LoginPage from './pages/Login/Login';
import PostQuery from './pages/PostQuery/PostQuery';
import BrowseQueries from './pages/BrowseQueries/BrowseQueries';
import QueryDetails from './pages/QueryDetails/QueryDetails';
import SearchResults from './pages/SearchResults/SearchResults';
import DashboardProfile from './pages/DashboardProfile/DashboardProfile';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import SetUsername from './pages/SetUsername/SetUsername';
import AdminPage from './pages/AdminPage/AdminPage';
import TermsAndConditions from './pages/TermsAndConditions/TermsAndConditions';
import SearchAnalytics from './pages/SearchAnalytics/SearchAnalytics';
import RewardsPage from './pages/Rewards/RewardsPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PerformanceMonitor from './components/utils/PerformanceMonitor';
import './theme.css';

function App() {
  return (
    <AuthProvider>
      <PerformanceMonitor />
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { userTheme, loading, error } = useAuth();
  
  // Apply theme when it changes
  useEffect(() => {
    if (userTheme) {
      document.body.className = userTheme.toLowerCase() + '-theme';
    } else {
      // Always apply default theme when no user is logged in
      document.body.className = 'default-theme';
      localStorage.removeItem('currentTheme');
    }
  }, [userTheme]);
  
  if (loading) {
    return (
      <div className="global-loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="global-error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }
  
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/post" element={<ProtectedRoute><PostQuery /></ProtectedRoute>} />
        <Route path="/browse" element={<BrowseQueries />} />
        <Route path="/query/:id" element={<QueryDetails />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardProfile /></ProtectedRoute>} />
        <Route path="/users/:userId" element={<DashboardProfile />} />
        <Route path="/username" element={<ProtectedRoute><SetUsername /></ProtectedRoute>} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/rewards" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} />
        <Route path="/vault" element={<Navigate to="/browse" replace />} />
        <Route path="/analytics" element={<ProtectedRoute><SearchAnalytics /></ProtectedRoute>} />
        
        {/* Admin routes */}
        <Route path="/admin" element={<ProtectedRoute adminOnly={true}><AdminPage /></ProtectedRoute>} />
        <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
        
        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer />
    </Router>
  );
}

// Add global styles to index.css
const globalStyles = `
.global-loading-container,
.global-error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  text-align: center;
  background-color: var(--body-bg-color);
  color: var(--text-color);
}

.global-error-container h2 {
  color: var(--danger-color);
  margin-bottom: 10px;
}

.global-error-container button {
  margin-top: 20px;
  padding: 10px 20px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
}

.global-error-container button:hover {
  background-color: var(--primary-hover-color);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

// Add styles to document
if (!document.getElementById('global-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'global-styles';
  styleElement.textContent = globalStyles;
  document.head.appendChild(styleElement);
}

export default App; 